import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';

const QRContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  max-width: 300px;
  margin: 20px auto;
`;

const QRTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.1rem;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QRCodeCanvas = styled.canvas`
  border-radius: 10px;
  background: white;
  padding: 10px;
  max-width: 200px;
  max-height: 200px;
`;

const ShareText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  text-align: center;
  margin: 0;
  line-height: 1.4;
`;

const URLText = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  font-family: monospace;
  font-size: 0.8rem;
  color: #4ecdc4;
  word-break: break-all;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CopyButton = styled(motion.button)`
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const QRCodeGenerator = ({ roomCode }) => {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  
  // Generate the room URL
  const roomUrl = `${window.location.origin}/room/${roomCode}`;
  
  useEffect(() => {
    if (canvasRef.current && roomCode) {
      try {
        // Generate QR code using the qrcode library
        QRCode.toCanvas(canvasRef.current, roomUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'H'
        }, (error) => {
          if (error) {
            console.error('Error generating QR code:', error);
          } else {
            console.log('QR code generated successfully');
          }
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  }, [roomCode, roomUrl]);
  
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(roomUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = roomUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  return (
    <QRContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <QRTitle>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="5" height="5"/>
          <rect x="16" y="3" width="5" height="5"/>
          <rect x="3" y="16" width="5" height="5"/>
          <path d="m21 16-3.5-3.5-2.5 2.5"/>
          <path d="m13 13 1 1"/>
          <path d="m16 16 1 1"/>
          <path d="m21 21-2-2"/>
        </svg>
        Scan to Join
      </QRTitle>
      
      <QRCodeCanvas ref={canvasRef} />
      
      <ShareText>
        Players can scan this QR code with their phone camera to quickly join the game room!
      </ShareText>
      
      <URLText>{roomUrl}</URLText>
      
      <CopyButton
        onClick={handleCopyUrl}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
        </svg>
        {copied ? 'Copied!' : 'Copy Link'}
      </CopyButton>
    </QRContainer>
  );
};

export default QRCodeGenerator; 